// Que tipo de documento es un archivo, mirando su nombre.
//
// La API solo acepta cuatro (`lib/types.ts:166`), y el tipo **no se deduce en
// el servidor**: si no se manda, la subida se rechaza. Quien sube desde
// Conocimiento lo elige en un desplegable; quien suelta un archivo sobre el
// Brain no elige nada, asi que hay que sacarlo de aqui.
//
// Lo que no reconoce devuelve `null` a proposito, en vez de caer en `txt`:
// mandar un `.zip` como texto no falla al subir, falla despues y sin decir por
// que.
//
// **Esto se escribia dos veces.** Hasta el 2026-09-23 el formulario tenia su
// propia `inferDocumentType` y aqui vivia la del arrastre, y **no hacian lo
// mismo**: esta acepta `.md` y aquella no. Dos respuestas distintas a la misma
// pregunta, cada una detras de una puerta. Lo encontro el reconocimiento del
// plan que iba a usar las dos a la vez.
//
// Por eso la lista de extensiones admitidas tambien sale de aqui: si la tabla
// dice que sabe leer un `.md` y el selector no lo deja elegir, vuelve a haber
// dos verdades.

import type { DocumentUploadData } from '@/lib/types'

type DocumentType = DocumentUploadData['document_type']

const BY_EXTENSION: Record<string, DocumentType> = {
  pdf: 'pdf',
  txt: 'txt',
  md: 'txt',
  csv: 'csv',
  docx: 'doc',
}

/** Lo que el Brain entiende sin archivarlo (brain-multimodal, D3 y D4): se
 *  describe o se transcribe una vez y su texto viaja con el mensaje. No es un
 *  documento, así que no entra en la tabla de arriba ni en el Knowledge.
 *
 *  Los límites se midieron el 2026-09-28 (1.7). NGINX corta en 50 MB y el
 *  archivo viaja en base64, un 33 % más: el techo real está en ~37 MB. */
export type MediaType = 'image' | 'audio' | 'video'

const MEDIA_BY_EXTENSION: Record<string, { type: MediaType; mime: string }> = {
  png: { type: 'image', mime: 'image/png' },
  jpg: { type: 'image', mime: 'image/jpeg' },
  jpeg: { type: 'image', mime: 'image/jpeg' },
  webp: { type: 'image', mime: 'image/webp' },
  gif: { type: 'image', mime: 'image/gif' },
  mp3: { type: 'audio', mime: 'audio/mpeg' },
  m4a: { type: 'audio', mime: 'audio/mp4' },
  wav: { type: 'audio', mime: 'audio/wav' },
  ogg: { type: 'audio', mime: 'audio/ogg' },
  opus: { type: 'audio', mime: 'audio/ogg' },
  mp4: { type: 'video', mime: 'video/mp4' },
  mov: { type: 'video', mime: 'video/quicktime' },
  webm: { type: 'video', mime: 'video/webm' },
}

/** Megas por tipo: una foto de móvil ocupa 2-6; 25 es lo máximo que acepta la
 *  transcripción de OpenAI; 30 queda por debajo del techo de 37. */
export const MAX_MEDIA_MB: Record<MediaType, number> = { image: 10, audio: 25, video: 30 }

export function mediaTypeOf(file: File): { type: MediaType; mime: string } | null {
  const extension = file.name.toLowerCase().split('.').pop() ?? ''
  const known = MEDIA_BY_EXTENSION[extension]
  return known ? { type: known.type, mime: file.type || known.mime } : null
}

export function isMediaTooBig(file: File, type: MediaType): boolean {
  return file.size > MAX_MEDIA_MB[type] * 1024 * 1024
}

/** Lo que un selector de archivos deja elegir, en el formato que espera el
 *  atributo `accept`. Son las dos tablas de arriba, para que no se separen. */
export const ACCEPTED_FILE_TYPES = [...Object.keys(BY_EXTENSION), ...Object.keys(MEDIA_BY_EXTENSION)]
  .map((extension) => `.${extension}`)
  .join(',')

/** Lo que el servidor admite, en megas. Hasta el 2026-09-23 este numero vivia
 *  escrito a mano en el formulario y **no existia en el camino guiado**: un
 *  archivo grande se subia entero para que el servidor lo rechazara al final,
 *  despues de la espera. Aqui lo leen los dos. */
export const MAX_UPLOAD_MB = 10

/** Si pesa mas de lo que se admite. Se pregunta antes de subir, no despues. */
export function isTooBig(file: File): boolean {
  return file.size > MAX_UPLOAD_MB * 1024 * 1024
}

const DOCUMENT_MIME: Record<DocumentType, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  doc: 'application/msword',
}

/** Para leer un documento sin guardarlo (brain-multimodal, D6), cuando el
 *  navegador no dice qué es. */
export function documentMimeOf(fileName: string): string {
  const type = documentTypeOf(fileName)
  return type ? DOCUMENT_MIME[type] : 'application/octet-stream'
}

export function documentTypeOf(fileName: string): DocumentType | null {
  const extension = fileName.toLowerCase().split('.').pop() ?? ''
  return BY_EXTENSION[extension] ?? null
}
